import { Core } from "@faire/design-tokens";
import { ICategoryTile } from "@faire/web-api/indigofair/data/ICategoryTile";
import { IImage } from "@faire/web-api/indigofair/data/IImage";
import { ILayoutElement } from "@faire/web-api/indigofair/data/ILayoutElement";
import { ILayoutElementData } from "@faire/web-api/indigofair/data/ILayoutElementData";
import { path as c1CategoryPath } from "@faire/web-api/routes/category/c1CategoryUrlId";
import { path as c2CategoryPath } from "@faire/web-api/routes/category/c1CategoryUrlId/subcategory/c2CategoryUrlId";
import { optimizeImageUrl } from "@faire/web/common/images/optimizeImages";
import { useStrictLocalization } from "@faire/web/common/localization";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import { Button } from "@faire/web/slate/Button";
import { Arrow } from "@faire/web/slate/icons/foundation/Arrow";
import { Column, Row } from "@faire/web/slate/Layout";
import { getSpacing } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";

import { AspectRatioPicture } from "@faire/retailer/components/Image/AspectRatioPicture";
import { LayoutElementBasedCarousel } from "@faire/retailer/components/LayoutElementRenderer/LayoutElementBasedCarousel";
import { getAllBrandsLink } from "@faire/retailer/consts/allBrandsLinks";
import { useCategoryStore } from "@faire/retailer/stores/domain/CategoryStore";
import { findActiveCategoryByUrlIds } from "@faire/retailer/stores/domain/CategoryStore/findByUrlId";

interface IPartialCategoryTile {
  displayName: string;
  categoryUrlIds: Array<string>;
  categoryImage: string;
}

const CATEGORY_IMAGE_RATIO = 0.75;
const useCategoryTilesFirstLine = (): Array<IPartialCategoryTile> => {
  const { strictLocalize } = useStrictLocalization();
  return [
    {
      displayName: strictLocalize({
        defaultMessage: "Men's Accessories",
        description: { text: "Category name" },
      }),
      categoryUrlIds: ["Men", "Accessories"],
      categoryImage:
        "https://cdn.faire.com/fastly/bc188be9274e8a254a9306dc6505e5e634812121f9f45cb7dcde77257310609c.png",
    },
    {
      displayName: strictLocalize({
        defaultMessage: "Paper & Novelty",
        description: { text: "Category name" },
      }),
      categoryUrlIds: ["Paper & Novelty"],
      categoryImage:
        "https://cdn.faire.com/fastly/621ef588c510f0b009987c620cb520c2d78ca6f330dee73d92dea18611b5cd87.png",
    },
    {
      displayName: strictLocalize({
        defaultMessage: "Food & Drink",
        description: { text: "Category name" },
      }),
      categoryUrlIds: ["Food & Drink"],
      categoryImage:
        "https://cdn.faire.com/fastly/683b6bb4c288bbf42456103a0dbb5da3d6b6baf65109a5a23513e1aa7f621787.jpeg",
    },
    {
      displayName: strictLocalize({
        defaultMessage: "Kids & Baby",
        description: { text: "Category name" },
      }),
      categoryUrlIds: ["Kids & Baby"],
      categoryImage:
        "https://cdn.faire.com/fastly/036f105590880fccb16557a23dc1c68979d4da72db385a75d189d331f9580b08.png",
    },
    {
      displayName: strictLocalize({
        defaultMessage: "Pets",
        description: { text: "Category name" },
      }),
      categoryUrlIds: ["Pets"],
      categoryImage:
        "https://cdn.faire.com/fastly/f864f1f3c36f1c92a5a508daf72d4dc32c6cc055114823578f2c6625ca44fafc.png",
    },
  ];
};
const useCategoryTilesSecondLine = (): Array<IPartialCategoryTile> => {
  const { strictLocalize } = useStrictLocalization();
  return [
    {
      categoryUrlIds: ["Women", "Accessories"],
      displayName: strictLocalize({
        defaultMessage: "Women's Apparel",
        description: { text: "Category name" },
      }),
      categoryImage:
        "https://cdn.faire.com/fastly/5f1f3fc93b6f3a83e868eacf60598645811be82135634509ba279f1964cca2b7.png",
    },
    {
      displayName: strictLocalize({
        defaultMessage: "Jewelry",
        description: { text: "Category name" },
      }),
      categoryUrlIds: ["Jewelry"],
      categoryImage:
        "https://cdn.faire.com/fastly/51a5c227c151db8ffb69b84b9fa3cdf25464042ecfd9befaca0db60cc437e304.png",
    },
    {
      displayName: strictLocalize({
        defaultMessage: "Beauty & Wellness",
        description: { text: "Category name" },
      }),
      categoryUrlIds: ["Beauty & Wellness"],
      categoryImage:
        "https://cdn.faire.com/fastly/94231fdb5dbaef1867c2a91a298c81133b0cb0b45b260e3befed981ff0b8467c.png",
    },
    {
      displayName: strictLocalize({
        defaultMessage: "Home Decor",
        description: { text: "Category name" },
      }),
      categoryUrlIds: ["Home Decor"],
      categoryImage:
        "https://cdn.faire.com/fastly/39599340837e784e77b48f40c98de2346954ce66dbed7bcf21509b424a08d672.png",
    },
  ];
};

export const IncentiveCategorySection: React.FC<{
  backgroundColor: string;
}> = ({ backgroundColor }) => {
  const { isMobile } = useViewport();
  return (
    <Wrapper $backgroundColor={backgroundColor}>
      <Typography color={Core.text.default} variant="displaySSerifRegular">
        <StrictLocalMsg
          defaultMessage="Shop by category"
          description={{
            text: "Header for category section",
            img: "https://cdn.faire.com/fastly/3aba6a3fbb1f7f19cce208c12f46011855652d1533c0515cba4c65861e30319f.png",
          }}
        />
      </Typography>
      <CategoryDisplay />
      <LinkWrapper>
        <SeeAllLink variant="tertiary" linkTo={getAllBrandsLink()}>
          <StrictLocalMsg
            defaultMessage="Shop all categories"
            description={{
              text: "Tertiary button for exploring the categories",
              img: "https://cdn.faire.com/fastly/3aba6a3fbb1f7f19cce208c12f46011855652d1533c0515cba4c65861e30319f.png",
            }}
          />
        </SeeAllLink>
        {isMobile ? (
          <Arrow width="16px" height="16px" direction="right" />
        ) : null}
      </LinkWrapper>
    </Wrapper>
  );
};

const CategoryDisplay: React.FC = () => {
  const { isMobile } = useViewport();
  const categoryTilesFirstLine = useCategoryTilesFirstLine();
  const categoryTilesSecondLine = useCategoryTilesSecondLine();
  const { categories } = useCategoryStore();

  if (isMobile) {
    const categoryTilesMobile = [
      ...categoryTilesFirstLine,
      ...categoryTilesSecondLine,
    ];
    const layoutData =
      categoryTilesMobile
        .map(({ categoryUrlIds, categoryImage, displayName }) => {
          const [c1, c2] = categoryUrlIds;
          if (!c1 || !findActiveCategoryByUrlIds(categoryUrlIds, categories)) {
            return;
          }
          return ILayoutElementData.build({
            category_tile: ICategoryTile.build({
              name: displayName,
              image: IImage.build({
                url: categoryImage,
                width: 145,
                height: 184,
              }),
              category_path_identifier: categoryUrlIds.join("|"),
              cta_link: c2 ? c2CategoryPath(c1, c2) : c1CategoryPath(c1),
            }),
          });
        })
        .filter(isNotUndefined) ?? [];
    const tallCategoryTilesLayoutElement = ILayoutElement.build({
      on_click_event_name: "tall_category_tiles",
      type: ILayoutElement.Type.TALL_CATEGORY_TILE,
      layout_element_data: layoutData,
    });

    return (
      <CarouselWrapper>
        <LayoutElementBasedCarousel
          layoutElement={tallCategoryTilesLayoutElement}
          requestId=""
          itemsPerSlide={2}
        />
      </CarouselWrapper>
    );
  }
  return (
    <Column gap="6x" align="center">
      <DesktopCategoriesSingleLineDisplay
        categoryTiles={categoryTilesFirstLine}
      />
      <DesktopCategoriesSingleLineDisplay
        categoryTiles={categoryTilesSecondLine}
      />
    </Column>
  );
};
const DesktopCategoriesSingleLineDisplay: React.FC<{
  categoryTiles: Array<IPartialCategoryTile>;
}> = ({ categoryTiles }) => {
  const { categories } = useCategoryStore();
  const { isTablet } = useViewport();
  const isSSR = useIsSSR();
  const { replace } = useHistory();
  return (
    <Row gap="2x">
      {categoryTiles.map(({ categoryUrlIds, categoryImage, displayName }) => {
        const [c1, c2] = categoryUrlIds;
        if (!c1 || !findActiveCategoryByUrlIds(categoryUrlIds, categories)) {
          return undefined;
        }
        const image = IImage.build({
          url: categoryImage,
        });
        const categoryPath = c2 ? c2CategoryPath(c1, c2) : c1CategoryPath(c1);
        return (
          <Column gap="2x" key={categoryUrlIds.join("|")}>
            <StyledAspectRatioPicture
              $width={isTablet ? "120px" : "188px"}
              $ratio={CATEGORY_IMAGE_RATIO}
              $hideBorder
              onClick={() => replace(categoryPath)}
            >
              <source
                srcSet={optimizeImageUrl(image, isSSR, {
                  width: 440,
                })}
                media="(min-width: 768px)"
              />
              <img
                src={optimizeImageUrl(image, isSSR, {
                  width: 750,
                })}
                alt={displayName}
              />
            </StyledAspectRatioPicture>
            <StyledNameTypography>{displayName}</StyledNameTypography>
          </Column>
        );
      })}
    </Row>
  );
};

const Wrapper = styled(Column).attrs({ gap: "4x", align: "center" })<{
  $backgroundColor: string;
}>`
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  ${responsiveMediaQueries.tabletAndBelow} {
    padding: ${getSpacing("6x", "3x")};
  }
  ${responsiveMediaQueries.desktopAndAbove} {
    padding: 96px 260px 96px 260px;
  }
`;

const StyledAspectRatioPicture = styled(AspectRatioPicture)`
  &:hover {
    cursor: pointer;
  }
`;

const SeeAllLink = styled(Button)`
  margin-top: ${getSpacing("2x")};
  ${responsiveMediaQueries.mobile} {
    margin-top: 0;
    justify-content: flex-start;
    margin-left: ${getSpacing("2x")};
  }
`;

const CarouselWrapper = styled(Column)`
  width: 100vw;
  margin: ${getSpacing("2x")};
`;

const LinkWrapper = styled(Row).attrs({
  gap: 4,
  align: "end",
})`
  ${responsiveMediaQueries.mobile} {
    width: 100vw;
  }
`;

const StyledNameTypography = styled(Typography)`
  ${responsiveMediaQueries.tablet} {
    width: 120px;
  }
  ${responsiveMediaQueries.desktopAndAbove} {
    width: 188px;
  }
`;
